/**
 * «Finalización automática» (#180, spec A7): the periods a company may choose, as one select for the two
 * screens that set it, *Empresa* and *Editar empresa*.
 *
 * It offers and shows; it decides nothing. The server holds the rule, both the values and the bound
 * against the company's download window (`422 AUTOCOMPLETE_PERIOD_EXCEEDS_WINDOW`), and each screen shows
 * its refusal. 10 and 14 days (#224) are offered to every company, and refused on a window shorter than
 * 17 and 21 days, which only ASGTRANS sets.
 */

import { useTranslation } from 'react-i18next'
import type { OrganisationSettings } from '@deca/api'

import { SelectInput } from '../ui/form.tsx'

/** One of the contract's periods, in hours. */
export type AutocompleteHours = NonNullable<OrganisationSettings['autocompleteHours']>

/** The periods in the contract's order: 2, 3, 4, 5, 7, 10 and 14 days. */
export const AUTOCOMPLETE_HOURS = [48, 72, 96, 120, 168, 240, 336] as const satisfies readonly AutocompleteHours[]

/** The column's default: what a body without the setting means. */
export const DEFAULT_AUTOCOMPLETE_HOURS: AutocompleteHours = 48

export function AutocompleteHoursSelect({
  value,
  onChange,
  disabled = false,
  busy = false,
}: {
  value: AutocompleteHours
  onChange: (hours: AutocompleteHours) => void
  disabled?: boolean
  busy?: boolean
}) {
  const { t } = useTranslation()
  return (
    <SelectInput
      label={t('admin.company.autocompleteHours')}
      help={t('admin.company.autocompleteHoursHelp')}
      value={String(value)}
      disabled={disabled}
      aria-busy={busy || undefined}
      // A number, as the contract types it: the option's value is a string.
      onChange={(event) => onChange(Number(event.currentTarget.value) as AutocompleteHours)}
    >
      {AUTOCOMPLETE_HOURS.map((hours) => (
        <option key={hours} value={hours}>
          {t(`admin.company.autocompleteOptions.${hours}`)}
        </option>
      ))}
    </SelectInput>
  )
}
